import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import * as API from '../api/students';

export interface Student {
    id: string;
    matricule: string;
    nom: string;
    prenom: string;
    dateNaissance: Date;
    lieuNaissance: string;
    sexe: 'M' | 'F';
    telephone?: string;
    email?: string;
    adresse?: string;
    niveau: '1' | '2' | '3' | '4';
    filiere: string;
    statut: 'actif' | 'inactif' | 'diplome' | 'abandonne';
    anneeAdmission: number;
    photo?: string;
    documents?: Record<string, any>;
    createdAt: Date;
    updatedAt: Date;
}

interface StudentsContextType {
    students: Student[];
    addStudent: (student: Omit<Student, 'id' | 'createdAt' | 'updatedAt' | 'matricule'>) => Promise<void>;
    updateStudent: (id: string, student: Partial<Student>) => Promise<void>;
    deleteStudent: (id: string) => Promise<void>;
    getStudentById: (id: string) => Student | undefined;
    getStudentsByStatut: (statut: string) => Student[];
    getStudentsByFiliere: (filiere: string) => Student[];
    isLoading: boolean;
}

const StudentsContext = createContext<StudentsContextType | undefined>(undefined);

export const useStudents = () => {
    const context = useContext(StudentsContext);
    if (context === undefined) {
        throw new Error('useStudents must be used within a StudentsProvider');
    }
    return context;
};

interface StudentsProviderProps {
    children: ReactNode;
}

export const StudentsProvider: React.FC<StudentsProviderProps> = ({ children }) => {
    const [students, setStudents] = useState<Student[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadStudents = async () => {
            try {
                setIsLoading(true);
                const data = await API.getStudents();
                const parsed = data.map((student: any) => ({
                    ...student,
                    dateNaissance: new Date(student.dateNaissance),
                    createdAt: new Date(student.createdAt),
                    updatedAt: new Date(student.updatedAt),
                }));
                setStudents(parsed);
            } catch (error) {
                console.error('Error loading students:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadStudents();
    }, []);

    const addStudent = async (studentData: Omit<Student, 'id' | 'createdAt' | 'updatedAt' | 'matricule'>) => {
        try {
            const newStudent: any = await API.createStudent({
                ...studentData,
                dateNaissance: studentData.dateNaissance.toISOString().split('T')[0],
            });

            const parsedStudent: Student = {
                ...newStudent,
                dateNaissance: new Date(newStudent.dateNaissance),
                createdAt: new Date(newStudent.createdAt),
                updatedAt: new Date(newStudent.updatedAt),
            };
            setStudents(prev => [parsedStudent, ...prev]);
        } catch (error) {
            console.error("Failed to create student", error);
            throw error;
        }
    };

    const updateStudent = async (id: string, updates: Partial<Student>) => {
        try {
            const updateData: any = { ...updates };
            if (updates.dateNaissance) {
                updateData.dateNaissance = updates.dateNaissance.toISOString().split('T')[0];
            }

            const updatedStudent: any = await API.updateStudent(id, updateData);
            const parsedStudent: Student = {
                ...updatedStudent,
                dateNaissance: new Date(updatedStudent.dateNaissance),
                createdAt: new Date(updatedStudent.createdAt),
                updatedAt: new Date(updatedStudent.updatedAt),
            };
            setStudents(prev =>
                prev.map(student => (student.id === id ? parsedStudent : student))
            );
        } catch (error) {
            console.error("Failed to update student", error);
            throw error;
        }
    };

    const deleteStudent = async (id: string) => {
        try {
            await API.deleteStudent(id);
            setStudents(prev => prev.filter(student => student.id !== id));
        } catch (error) {
            console.error("Failed to delete student", error);
            throw error;
        }
    };

    const getStudentById = (id: string) => {
        return students.find(student => student.id === id);
    };

    const getStudentsByStatut = (statut: string) => {
        return students.filter(student => student.statut === statut);
    };

    const getStudentsByFiliere = (filiere: string) => {
        return students.filter(student => student.filiere === filiere);
    };

    const value: StudentsContextType = {
        students,
        addStudent,
        updateStudent,
        deleteStudent,
        getStudentById,
        getStudentsByStatut,
        getStudentsByFiliere,
        isLoading
    };

    return (
        <StudentsContext.Provider value={value}>
            {children}
        </StudentsContext.Provider>
    );
};
